import {inject, Injectable} from '@angular/core';
import {AssignmentInterface} from '@shared/interfaces/assignment.interface';
import {AppExamActions, ContentExamMetadataSuccessProperties} from '@component/exam/redux/exam.actions';
import {AppExamSelectors} from '@component/exam/redux/exam.selectors';
import {AppExamActionsEnum} from '@component/exam/redux/exam.enum';
import {ActionsSubject, Store} from '@ngrx/store';
import {filter, mergeMap, switchMap, take} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, OperatorFunction, Subscription} from 'rxjs';
import {AssignmentDocumentDetailsInterface} from '@core/services/assignment/assignment.service';
import {ofType} from '@ngrx/effects';
import {UserInterface} from '@shared/interfaces/user.interface';
import {AppTranslateService} from '@core/services/translate/translate.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponseInterface, HttpValidationErrorResponseContextInterface, HttpValidationErrorResponseInterface} from '@core/interfaces/http-error-response.interface';

export enum AppExamType {
    Practice = 10,
    Real = 20,
}

export interface AppExamStepResult {
    score: number;
    dpsId: string;
    title: string;
}

export interface AppExamResult {
    assigmentResults: AppExamStepResult[];
    id: number;
    planId: number;
    completed: string;
    totalScore: number;
}

/**
 * @see https://xiponlineapplications.atlassian.net/browse/BD-1997
 */
export interface AppExamMetadata {
    assignments: AssignmentInterface[];
    bookUuid: string;
    chapterUuid: string;
    documentDpsId: string;
    examPlanId: number;
    inProgress: boolean;
    title: string;
    type: AppExamType;
    maxAttempts?: number;
    results?: AppExamResult[];
    user?: UserInterface;
}

export interface AppExamStep {
    bookUuid: string;
    chapterUuid: string;
    document: AssignmentDocumentDetailsInterface;
}

@Injectable({
    providedIn: 'root',
})
export class AppExamPlayerService {
    readonly #store: Store = inject(Store);

    readonly #snackbar = inject(MatSnackBar);

    readonly #storeActionsSubject: ActionsSubject = inject(ActionsSubject);

    readonly #translateService: AppTranslateService = inject(AppTranslateService);

    readonly #assignmentSubject: BehaviorSubject<AppExamStep | undefined> = new BehaviorSubject<AppExamStep | undefined>(undefined);

    readonly #metadataSubject: BehaviorSubject<AppExamMetadata | undefined> = new BehaviorSubject<AppExamMetadata | undefined>(undefined);

    constructor() {
        this.#store.select(AppExamSelectors.selectStep).pipe(
            filter((examStep?: AppExamStep): boolean => undefined !== examStep) as OperatorFunction<AppExamStep | undefined, AppExamStep>,
        ).subscribe((examStep: AppExamStep) => this.#assignmentSubject.next(examStep));

        this.#store.select(AppExamSelectors.selectMetadata).pipe(
            filter((metadata?: AppExamMetadata): boolean => undefined !== metadata) as OperatorFunction<AppExamMetadata | undefined, AppExamMetadata>,
        ).subscribe((metadata: AppExamMetadata) => this.#metadataSubject.next(metadata));

        // We'll handle all errors here
        this.#storeActionsSubject.pipe(ofType(AppExamActionsEnum.StartFailed, AppExamActionsEnum.FinishFailed)).subscribe(properties => this.#handleFailedRequest(properties));
    }

    subscribeToStep(fn: (examStep: AppExamStep) => void): Subscription {
        return this.#assignmentSubject.pipe(
            filter((examStep?: AppExamStep): boolean => undefined !== examStep) as OperatorFunction<AppExamStep | undefined, AppExamStep>,
        ).subscribe((examStep: AppExamStep) => fn(examStep));
    }

    subscribeToMetadata(fn: (metadata: AppExamMetadata) => void): Subscription {
        return this.#metadataSubject.pipe(
            filter((metadata?: AppExamMetadata): boolean => undefined !== metadata) as OperatorFunction<AppExamMetadata | undefined, AppExamMetadata>,
        ).subscribe((metadata: AppExamMetadata) => fn(metadata));
    }

    load(page: number, userId?: number): Observable<AppExamMetadata> {
        return this.#metadataSubject.pipe(
            filter((metadata?: AppExamMetadata): boolean => undefined !== metadata) as OperatorFunction<AppExamMetadata | undefined, AppExamMetadata>,
            mergeMap((metadata: AppExamMetadata) => of(this.#loadStep(metadata, page, userId))),
            take(1),
        );
    }

    start(metadata: AppExamMetadata): Observable<AppExamMetadata> {
        const observable: Observable<AppExamMetadata> = this.#storeActionsSubject.pipe(
            ofType(AppExamActionsEnum.StartSuccess),
            switchMap((properties: ContentExamMetadataSuccessProperties) => of(properties.metadata)),
            take(1),
        );

        this.#store.dispatch(AppExamActions.start({bookUuid: metadata.bookUuid, chapterUuid: metadata.chapterUuid, documentDpsId: metadata.documentDpsId, examPlanId: metadata.examPlanId}));

        return observable;
    }

    finish(metadata: AppExamMetadata): Observable<AppExamMetadata> {
        const observable: Observable<AppExamMetadata> = this.#storeActionsSubject.pipe(
            ofType(AppExamActionsEnum.FinishSuccess),
            switchMap((properties: ContentExamMetadataSuccessProperties) => of(properties.metadata)),
            take(1),
        );

        this.#store.dispatch(AppExamActions.finish({bookUuid: metadata.bookUuid, chapterUuid: metadata.chapterUuid, documentDpsId: metadata.documentDpsId, examPlanId: metadata.examPlanId}));

        return observable;
    }

    clear(): void {
        this.#store.dispatch(AppExamActions.clear());
    }

    #loadStep(metadata: AppExamMetadata, page: number, userId?: number): AppExamMetadata {
        const resultCount: number = metadata.results?.length ?? 0;
        const attempt: number = metadata.inProgress ? resultCount + 1 : resultCount;

        if (page <= metadata.assignments.length) {
            this.#store.dispatch(AppExamActions.step({
                bookUuid: metadata.bookUuid,
                chapterUuid: metadata.chapterUuid,
                documentDpsId: metadata.documentDpsId,
                examPlanId: metadata.examPlanId,
                assignmentDpsId: metadata.assignments[page - 1].dpsId,
                userId,
                attempt,
            }));
        }

        return metadata;
    }

    #handleFailedRequest(properties: HttpErrorResponseInterface): void {
        if (!this.#isRequestFailedResponse(properties) || !properties.hasOwnProperty('error')) {
            return;
        }

        const error: HttpValidationErrorResponseContextInterface = properties.error.context[0];
        const message: string = this.#translateService.instant(error.code, error.data);

        this.#snackbar.open(message, '', {duration: 20000, horizontalPosition: 'right', verticalPosition: 'top', panelClass: ['red-snackbar']});
    }

    #isRequestFailedResponse(properties: HttpErrorResponseInterface): properties is HttpValidationErrorResponseInterface {
        return properties.error.hasOwnProperty('context');
    }
}
